/*
* This code is the intellectual property of Bulb Inc. and other software publishers;
* it may not be altered, copied or disclosed without prior written approval.
* Copyright 2022, Bulb Inc. For more information, please email contact@hellobulb.com.
*/

/* eslint-disable no-console */
import {
  AfterViewInit, Directive, ElementRef, HostListener, Inject, Renderer2, RendererStyleFlags2,
} from '@angular/core';
import { WINDOW } from 'src/common/services/window.service';

/**
 * A directive that applies a CSS variable to the host element based on the scroll position of the window.
 */
@Directive({
  selector: '[bulbHeaderEffects]',
  standalone: false,
})
export class HeaderEffectsDirective implements AfterViewInit {

  private effectCSSPropName = '--bulb-header-effect-progress';

  private headerDimensions;

  private headerHeight = 0;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    @Inject(WINDOW) private window: Window,
  ) { }

  ngAfterViewInit(): void {
    this.headerDimensions = document.querySelector('.bulb-page-header-container')?.getBoundingClientRect();
    if (this.headerDimensions) {
      this.headerHeight = this.headerDimensions.height + this.headerDimensions.top;
    }
    this.setEffectCSSProps();
  }

  @HostListener('window:scroll', ['$event'])
  onScroll() {
    this.window.requestAnimationFrame(() => {
      this.setEffectCSSProps();
    });
  }

  /**
   * Given this element's current position on screen,
   * set a CSS Custom Property on the element to be used in CSS transitions.
   */
  private setEffectCSSProps() {
    // eslint-disable-next-line prefer-const
    let { top, height } = this.el.nativeElement.getBoundingClientRect();
    // A value in range [0,1] that represents how far progress the effect has made
    // 0: the effect hasn't started
    // 1: the effect is complete
    let percentInView;

    top -= this.headerHeight;
    // Set bounds for the percentInView value
    // We don't want to go below 0 or above 1
    if (top > 0) {
      percentInView = 1;
    } else if (Math.abs(top) >= height) {
      percentInView = 0;
    } else {
      percentInView = Math.max((height - top) / height, 0);
    }
    percentInView = (1 + percentInView) % 1;
    const effectProgress = String(percentInView);
    this.renderer.setStyle(
      this.el.nativeElement,
      this.effectCSSPropName,
      effectProgress,
      RendererStyleFlags2.DashCase,
    );
  }
}
